/* eslint-disable typescript-enum/no-enum */

import type {
  IAgoraRTC,
  IRemoteAudioTrack,
  IRemoteVideoTrack,
  SDK_CODEC,
  UID,
  ScreenEncoderConfigurationPreset,
  SVCConfigurationPreset,
  LowStreamParameter,
} from 'agora-rtc-sdk-ng';

import { Maybe, Optional } from '@vimeo/core/types/generic';

export type TAgoraSDK = IAgoraRTC;

export type TMediaCodec = SDK_CODEC;

export type TScreenEncoderPreset = ScreenEncoderConfigurationPreset;

export type TScreenSVCPreset = SVCConfigurationPreset;

export type TDualStreamConfig = LowStreamParameter;

export enum EStreamQuality {
  HIGH = 0,
  LOW = 1,
}

export enum EClientRole {
  HOST = 'host',
  AUDIENCE = 'audience',
}

export enum EAgoraMode {
  LIVE = 'live',
  RTC = 'rtc',
}

export enum EAgoraProxyMode {
  /**
   * Default proxy mode.
   * Let agora decide based on application admin panel configuration.
   */
  AUTO = 'auto',
  /**
   * `3`: The cloud proxy for the UDP protocol, that is, the Force UDP cloud proxy mode.
   * In this mode, the SDK always transmits data over UDP.
   */
  FORCE_UDP = 'force_udp',
  /**
   * `5`: The cloud proxy for the TCP (encryption) protocol, that is, the Force TCP cloud proxy mode.
   * In this mode, the SDK always transmits data over TLS 443.
   */
  FORCE_TCP = 'force_tcp',
}

/**
 * Internal exception codes for agora library.
 */
export enum EAgoraErrorCode {
  OPERATION_ABORTED = 'OPERATION_ABORTED',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  CONSTRAINT_NOT_SATISFIED = 'CONSTRAINT_NOT_SATISFIED',
  TRACK_IS_DISABLED = 'TRACK_IS_DISABLED',
  NO_DEVICES = 'NO_DEVICES',
  CAN_NOT_GET_GATEWAY_SERVER = 'CAN_NOT_GET_GATEWAY_SERVER',
  UNEXPECTED_ERROR = 'UNEXPECTED_ERROR',
  UNEXPECTED_RESPONSE = 'UNEXPECTED_RESPONSE',
  NOT_READABLE = 'NOT_READABLE',
}

export interface IAgoraError {
  readonly code: EAgoraErrorCode;
  readonly message: string;
  readonly name: string;
}

/**
 0	The network quality is unknown.
 1	The network quality is excellent.
 2	The network quality is good, but the bitrate may be slightly lower than optimal.
 3	Users experience slightly impaired communication.
 4	Users cannot communicate smoothly.
 5	The network is so poor that users can barely communicate.
 6	The network is down and users cannot communicate at all.
 */
export enum EAgoraNetworkQuality {
  UNKNOWN = 0,
  EXCELLENT = 1,
  GOOD = 2,
  IMPAIRED = 3,
  BAD = 4,
  POOR = 5,
  DOWN = 6,
}

export enum EAgoraConnectionType {
  BROADCASTER = 'BROADCASTER',
  PREVIEW = 'PREVIEW',
  MEDIA = 'MEDIA',
  GUEST = 'GUEST',
  RTMP = 'RTMP',
  SCREEN = 'SCREEN',
  GUEST_SCREEN = 'GUEST_SCREEN',
  BROADCASTER_SCREEN = 'BROADCASTER_SCREEN',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Mapping enum for agora connection states.
 * (type ConnectionState)
 */
export enum EAgoraConnectionState {
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  DISCONNECTING = 'DISCONNECTING',
  RECONNECTING = 'RECONNECTING',
}

export interface IAgoraDualConnectionConfig {
  appid: string;
  channel: string;
  cloudProxy: Maybe<EAgoraProxyMode>;
  uid: UID;
  media: {
    token: string;
    ttl: number;
    uid: UID;
  };
  screen: {
    token: string;
    ttl: number;
    uid: UID;
  };
}

export interface IAgoraRemoteMediaDescriptor {
  uid: UID;
  // Type of agora connection (parsed from connection UID).
  type: EAgoraConnectionType;
  video: Optional<IRemoteVideoTrack>;
  audio: Optional<IRemoteAudioTrack>;
  quality: EStreamQuality;
  volume: number;
  connectedAt: number;
}

export type TAgoraConnectionDetails = [EAgoraConnectionType, string];

export interface IAgoraUserVolume {
  uid: UID;
  level: number;
}

export type TAgoraRoomVolume = Record<UID, IAgoraUserVolume>;

/**
 * Enumeration of agora external parameters used for global SDK settings.
 */
export enum EAgoraParameter {
  // Interval to poll agora room volume.
  AUDIO_VOLUME_INDICATION_INTERVAL = 'AUDIO_VOLUME_INDICATION_INTERVAL',
}
